import { useCallback, useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { formatDateShort } from '@/lib/date';
import { getStatusMeta, lessonStatusMap, paymentStatusMap } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';

export interface StudentDetail {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  level: string | null;
  group_id: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  register_date: string | null;
  notes: string | null;
  group_name: string | null;
}

interface LessonRow {
  id: string;
  title: string;
  date: string | null;
  start_time: string | null;
  status: string;
}

interface SimpleItem {
  id: string;
  title: string;
}

interface PackageRow {
  id: string;
  type: string | null;
  name: string | null;
  total_sessions: number | null;
  used_sessions: number | null;
  remaining_sessions: number | null;
  payment_status: string | null;
}

interface PaymentRow {
  id: string;
  amount: number | null;
  status: string | null;
  date: string | null;
}

interface StudentDetailModalProps {
  student: StudentDetail | null;
  onClose: () => void;
}

type Tab = 'genel' | 'dersler' | 'kayitlar' | 'dokumanlar' | 'odevler' | 'paket';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'genel', label: 'Genel Bilgiler', icon: 'ri-information-line' },
  { key: 'dersler', label: 'Dersleri', icon: 'ri-book-open-line' },
  { key: 'kayitlar', label: 'Ders Kayıtları', icon: 'ri-vidicon-line' },
  { key: 'dokumanlar', label: 'Dokümanları', icon: 'ri-file-text-line' },
  { key: 'odevler', label: 'Ödevleri', icon: 'ri-task-line' },
  { key: 'paket', label: 'Paket / Ödeme', icon: 'ri-wallet-3-line' },
];

export default function StudentDetailModal({ student, onClose }: StudentDetailModalProps) {
  const [tab, setTab] = useState<Tab>('genel');
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [records, setRecords] = useState<SimpleItem[]>([]);
  const [docs, setDocs] = useState<SimpleItem[]>([]);
  const [assignments, setAssignments] = useState<SimpleItem[]>([]);
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!student) return;
    setLoading(true);
    try {
      const groupId = student.group_id;
      const [
        { data: lessonsData },
        { data: recordsData },
        { data: docsData },
        { data: assignmentsData },
        { data: packagesData },
        { data: paymentsData },
      ] = await Promise.all([
        supabase.from('zehrahoca_lessons').select('id, title, date, start_time, status').eq('group_id', groupId).order('date', { ascending: false }),
        supabase.from('zehrahoca_lesson_records').select('id, title').eq('group_id', groupId),
        supabase.from('zehrahoca_documents').select('id, title').eq('group_id', groupId),
        supabase.from('zehrahoca_assignments').select('id, title').eq('group_id', groupId),
        supabase.from('zehrahoca_packages').select('id, type, name, total_sessions, used_sessions, remaining_sessions, payment_status').eq('student_id', student.id),
        supabase.from('zehrahoca_payments').select('id, amount, status, date').eq('student_id', student.id),
      ]);
      setLessons((lessonsData ?? []) as LessonRow[]);
      setRecords((recordsData ?? []) as SimpleItem[]);
      setDocs((docsData ?? []) as SimpleItem[]);
      setAssignments((assignmentsData ?? []) as SimpleItem[]);
      setPackages((packagesData ?? []) as PackageRow[]);
      setPayments((paymentsData ?? []) as PaymentRow[]);
    } catch {
      // boş bırak
    } finally {
      setLoading(false);
    }
  }, [student]);

  useEffect(() => {
    if (student) {
      setTab('genel');
      fetchAll();
    }
  }, [student, fetchAll]);

  if (!student) return null;

  const initial = (student.full_name || '?').trim().charAt(0).toUpperCase();
  const registerDate = student.register_date
    ? new Date(student.register_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  return (
    <Modal
      open={!!student}
      onClose={onClose}
      title={student.full_name}
      subtitle={student.group_name ? `${student.group_name} grubu` : 'Öğrenci detayları'}
      maxWidth="max-w-3xl"
    >
      <div className="space-y-5">
        <div className="flex flex-wrap gap-1 rounded-full bg-background-100 p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition cursor-pointer ${
                tab === t.key ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              <i className={t.icon} />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
          </div>
        ) : (
          <>
            {tab === 'genel' && (
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 font-heading text-2xl font-bold text-primary-700">
                    {initial}
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold text-foreground-950">{student.full_name}</p>
                    <span
                      className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        student.active ? 'bg-emerald-50 text-emerald-600' : 'bg-background-100 text-foreground-500'
                      }`}
                    >
                      <i className={student.active ? 'ri-checkbox-circle-fill' : 'ri-pause-circle-line'} />
                      {student.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </div>

                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    { label: 'E-posta', value: student.email ?? '—', icon: 'ri-mail-line' },
                    { label: 'Telefon', value: student.phone ?? '—', icon: 'ri-phone-line' },
                    { label: 'Veli Adı', value: student.parent_name ?? '—', icon: 'ri-user-heart-line' },
                    { label: 'Veli Telefonu', value: student.parent_phone ?? '—', icon: 'ri-phone-line' },
                    { label: 'Seviye', value: student.level ?? '—', icon: 'ri-bar-chart-2-line' },
                    { label: 'Grup', value: student.group_name ?? '—', icon: 'ri-team-line' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 rounded-lg bg-background-50 p-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-foreground-500">
                        <i className={item.icon} />
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="text-sm font-semibold text-foreground-900">{item.value}</p>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center gap-3 rounded-lg bg-background-50 p-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-foreground-500">
                      <i className="ri-calendar-line" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">Kayıt Tarihi</p>
                      <p className="text-sm font-semibold text-foreground-900">{registerDate}</p>
                    </div>
                  </div>
                </dl>

                {student.notes && (
                  <div className="rounded-lg bg-accent-50 p-4">
                    <p className="text-xs font-semibold text-accent-800">Notlar</p>
                    <p className="mt-1 text-sm text-foreground-700">{student.notes}</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'dersler' && (
              <div className="space-y-2">
                {lessons.length === 0 ? (
                  <EmptyState text="Bu öğrencinin grubuna ait ders bulunamadı." />
                ) : (
                  lessons.map((l) => (
                    <div key={l.id} className="flex items-center justify-between rounded-lg border border-background-200 p-3">
                      <p className="text-sm font-medium text-foreground-900">{l.title}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-foreground-500">
                          {l.date ? formatDateShort(l.date) : '—'}
                          {l.start_time ? ` · ${l.start_time}` : ''}
                        </span>
                        <StatusBadge meta={getStatusMeta(l.status, lessonStatusMap)} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {(tab === 'kayitlar' || tab === 'dokumanlar' || tab === 'odevler') && (
              <div className="space-y-2">
                {(() => {
                  const list = tab === 'kayitlar' ? records : tab === 'dokumanlar' ? docs : assignments;
                  if (list.length === 0) return <EmptyState text="Bu grupta henüz içerik yok." />;
                  return list.map((item) => (
                    <div key={item.id} className="flex items-center justify-between rounded-lg border border-background-200 p-3">
                      <p className="text-sm font-medium text-foreground-900">{item.title}</p>
                    </div>
                  ));
                })()}
              </div>
            )}

            {tab === 'paket' && (
              <div className="space-y-5">
                <div>
                  <p className="mb-2 text-sm font-semibold text-foreground-800">Paketler</p>
                  {packages.length === 0 ? (
                    <EmptyState text="Bu öğrenciye atanmış paket yok." />
                  ) : (
                    <div className="space-y-2">
                      {packages.map((p) => (
                        <div key={p.id} className="rounded-lg border border-background-200 p-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-foreground-900">{p.name ?? p.type ?? 'Paket'}</p>
                            {p.payment_status && (
                              <StatusBadge meta={getStatusMeta(p.payment_status, paymentStatusMap)} />
                            )}
                          </div>
                          <p className="mt-1 text-xs text-foreground-500">
                            {p.used_sessions ?? 0} / {p.total_sessions ?? 0} seans · {p.remaining_sessions ?? 0} kalan
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-foreground-800">Ödemeler</p>
                  {payments.length === 0 ? (
                    <EmptyState text="Bu öğrenciye ait ödeme kaydı yok." />
                  ) : (
                    <div className="space-y-2">
                      {payments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between rounded-lg border border-background-200 p-3">
                          <div>
                            <p className="text-sm font-semibold text-foreground-900">₺{p.amount ?? 0}</p>
                            <p className="text-xs text-foreground-500">{p.date ? formatDateShort(p.date) : '—'}</p>
                          </div>
                          {p.status && <StatusBadge meta={getStatusMeta(p.status, paymentStatusMap)} />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed border-background-300 p-8 text-center">
      <p className="text-xs text-foreground-500">{text}</p>
    </div>
  );
}